import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HERO_IMG, LOGO } from "@/lib/site-data";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.45 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Phillips crew at work on a concrete project"
          className="h-[115%] w-full object-cover"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#0A0A0A]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,102,255,0.18),transparent_55%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full pt-36 pb-44">
        <motion.img
          src={LOGO}
          alt="Phillips Waterproofing & Concrete logo"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="h-20 md:h-28 w-auto mb-8 drop-shadow-[0_0_25px_rgba(0,102,255,0.45)]"
        />

        <div className="overflow-hidden">
          <motion.p
            custom={0}
            variants={lineVariants}
            initial="hidden"
            animate="visible"
            className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-cyan-400 mb-5"
          >
            Waterproofing · Foundation Repair · Concrete — Kentucky
          </motion.p>
        </div>

        <h1 className="font-display font-semibold tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-[5.2rem]">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block">
              Protecting What You Built.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              custom={2}
              variants={lineVariants}
              initial="hidden"
              animate="visible"
              className="block bg-gradient-to-r from-[#00E5FF] via-[#38b6ff] to-[#0066FF] bg-clip-text text-transparent"
            >
              Building What Lasts.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-6 text-lg md:text-xl font-semibold text-white/90"
        >
          Professional Waterproofing, Foundation Repair &amp; Concrete Services
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.12, duration: 0.8 }}
          className="mt-3 max-w-xl text-base md:text-lg text-gray-400"
        >
          Phillips provides long-term solutions for homes and businesses throughout Kentucky and
          surrounding communities.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#estimate"
            data-testid="hero-get-estimate-btn"
            className="group relative inline-flex items-center gap-2 rounded-md bg-[#0066FF] px-8 py-4 font-display text-sm font-semibold tracking-widest text-white transition-[background-color,box-shadow,color] duration-300 hover:bg-[#00E5FF] hover:text-black hover:shadow-[0_0_35px_rgba(0,229,255,0.55)]"
          >
            GET A FREE ESTIMATE
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#projects"
            data-testid="hero-view-projects-btn"
            className="inline-flex items-center gap-2 rounded-md border-2 border-white/25 px-8 py-[14px] font-display text-sm font-semibold tracking-widest text-white transition-[border-color,box-shadow] duration-300 hover:border-cyan-400 hover:shadow-[0_0_25px_rgba(0,229,255,0.3)]"
          >
            VIEW OUR PROJECTS
          </a>
        </motion.div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 h-[2px] w-56 origin-left bg-gradient-to-r from-[#00E5FF] to-transparent"
        />
      </motion.div>

      <motion.a
        href="#services"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cyan-400/80"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown size={26} />
        </motion.div>
      </motion.a>
    </section>
  );
}
